import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {RemoteSession} from '@PosterWhiteboard/collaboration/poster-presence.types';
import type {RemoteSelectionOutline} from './page-presence-layer.types';
import {FULL_TURN_DEGREES, NAME_PILL_FLIP_FROM_DEGREES, NAME_PILL_FLIP_UNTIL_DEGREES} from './page-presence-layer.types';

/**
 * One box around the session's selected items, measured from this editor's Fabric objects, so the
 * outline sits on the items as this viewer has them and moves only when the other editor's edit is
 * applied here. A single item is boxed at its own angle; the members of one saved group as the
 * group, at the group's angle; any other selection of several items by the upright rectangle
 * around them, as this editor's own selection box would show each. Null when none of the ids is
 * known here. `itemTransformVersion` is taken only so the caller's result follows every move,
 * scale, rotate and applied remote edit.
 */
export const getRemoteSelectionOutline = (session: RemoteSession, itemTransformVersion: number): RemoteSelectionOutline | null => {
  return measureSelectionOutline(session.selectedItemIds, itemTransformVersion);
};

/**
 * Whether a name pill turning with the outline would read upside down at this angle. The layer then
 * hangs the pill off the outline's bottom-right corner turned half round, so the name is never
 * further than a quarter turn from upright.
 */
export const isNamePillFlipped = (angle: number): boolean => {
  const turn = ((angle % FULL_TURN_DEGREES) + FULL_TURN_DEGREES) % FULL_TURN_DEGREES;
  return turn > NAME_PILL_FLIP_FROM_DEGREES && turn < NAME_PILL_FLIP_UNTIL_DEGREES;
};

const measureSelectionOutline = (itemIds: string[], _itemTransformVersion: number): RemoteSelectionOutline | null => {
  const items = findKnownItems(itemIds);
  if (items.length === 0) {
    return null;
  }
  if (items.length === 1) {
    return measureItemOutline(items[0]);
  }
  return measureGroupOutline(items) ?? measureUprightOutlineAround(items);
};

const findKnownItems = (itemIds: string[]): ItemType[] => {
  return itemIds.flatMap((itemId): ItemType[] => {
    const item = window.posterEditor?.whiteboard?.getItemForId(itemId);
    return item === undefined ? [] : [item];
  });
};

/** Measured in page space through the item's absolute getters, so a member of this viewer's own active selection is boxed where it sits on the page, not where it sits in that selection. */
const measureItemOutline = (item: ItemType): RemoteSelectionOutline => {
  const center = item.fabricObject.getCenterPoint();
  const width = item.getFabricObjectAbsoluteScaledWidth();
  const height = item.getFabricObjectAbsoluteScaledHeight();
  return {left: center.x - width / 2, top: center.y - height / 2, width, height, angle: item.getFabricObjectAbsoluteAngle()};
};

/** The box this editor would draw around the same saved group; null when the items are not all members of one group. */
const measureGroupOutline = (items: ItemType[]): RemoteSelectionOutline | null => {
  const {groupId} = items[0];
  if (groupId === null || !items.every((item) => item.groupId === groupId)) {
    return null;
  }
  return items[0].page.items.groups.getBoundingBoxForGroup(groupId) ?? null;
};

const measureUprightOutlineAround = (items: ItemType[]): RemoteSelectionOutline => {
  const rects = items.map((item) => item.fabricObject.getBoundingRect());
  const left = Math.min(...rects.map((rect) => rect.left));
  const top = Math.min(...rects.map((rect) => rect.top));
  const right = Math.max(...rects.map((rect) => rect.left + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.top + rect.height));
  return {left, top, width: right - left, height: bottom - top, angle: 0};
};
